// Static catalog of GeoJSON-overlay specs consumed by overlayRuntime.
//
// One entry per overlay; each carries the URL builder + fetch options
// so MapView only has to know about (a) the source/layer wiring and
// (b) which OverlayId to refresh.
//
// Adding a new GeoJSON overlay = one entry here + a source/layer pair
// in MapView's addOverlays() + an entry in pushCachedOverlays() (see
// MapView's setStyle handler).

import type { OverlaySpec } from './overlayRuntime';
import type { Map as MlMap } from 'maplibre-gl';

/** Read the current viewport as west,south,east,north (lon,lat). */
function bboxWsEn(map: MlMap): string {
  const b = map.getBounds();
  return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
    .map((n) => n.toFixed(4))
    .join(',');
}

/** AWC's bbox order is minLat,minLon,maxLat,maxLon. Our internal
 *  bboxWsEn is west,south,east,north. Swap on the way to AWC. */
function bboxAwc(map: MlMap): string {
  const [w, s, e, n] = bboxWsEn(map).split(',').map(parseFloat);
  return `${s},${w},${n},${e}`;
}

export const OVERLAY_SPECS: ReadonlyArray<OverlaySpec> = [
  {
    id: 'fires',
    sourceId: 'fires',
    statusKey: 'fires',
    buildUrl: (m) => `/api/fires?bbox=${encodeURIComponent(bboxWsEn(m))}`,
  },
  {
    id: 'tfr',
    sourceId: 'tfr',
    statusKey: 'tfr',
    buildUrl: () => '/api/tfr',
    // Live operational data; bypass any HTTP cache.
    fetchInit: { cache: 'no-store' },
  },
  {
    id: 'sua',
    sourceId: 'sua',
    statusKey: 'sua',
    buildUrl: (m) => `/api/sua?bbox=${encodeURIComponent(bboxWsEn(m))}`,
  },
  {
    id: 'viirs',
    sourceId: 'viirs',
    statusKey: 'viirs',
    buildUrl: (m) => `/api/viirs?bbox=${encodeURIComponent(bboxWsEn(m))}`,
  },
  {
    id: 'airmet',
    sourceId: 'airmet',
    statusKey: 'airmet',
    buildUrl: () => '/api/airmet',
  },
  {
    id: 'sigmet',
    sourceId: 'sigmet',
    statusKey: 'sigmet',
    buildUrl: () => '/api/sigmet',
  },
  {
    id: 'gfa',
    sourceId: 'gfa',
    statusKey: 'gfa',
    buildUrl: () => '/api/wx/gfa',
  },
  {
    id: 'cwa',
    sourceId: 'cwa',
    statusKey: 'cwa',
    buildUrl: () => '/api/wx/cwa',
  },
  {
    id: 'flightcat',
    sourceId: 'wxstations',
    statusKey: 'flightcat',
    buildUrl: (m) => `/api/wx/stations?bbox=${encodeURIComponent(bboxAwc(m))}`,
  },
  {
    id: 'pireps',
    sourceId: 'pireps',
    statusKey: 'pireps',
    // age=2 matches the proxy default; surfaced explicitly here so a
    // future per-layer override is obvious.
    buildUrl: (m) => `/api/wx/pirep?bbox=${encodeURIComponent(bboxAwc(m))}&age=2`,
  },
  {
    id: 'dispatch',
    sourceId: 'dispatch-zones',
    statusKey: 'dispatch',
    // National polygon set, ~1–2 MB simplified. Fetched once per
    // session; no bbox — we want full national coverage so the
    // boundary-crossing alert works wherever the user is.
    buildUrl: () => '/api/dispatch-zones',
  },
  {
    id: 'obstacles',
    sourceId: 'obstacles',
    statusKey: 'obstacles',
    // FAA DOF point set. We bbox-filter (>=636k national rows trimmed
    // to whatever the viewport intersects, typically <2k features).
    buildUrl: (m) => `/api/obstacles?bbox=${encodeURIComponent(bboxWsEn(m))}&minAglFt=200`,
  },
  {
    // NIFC aviation-frequency repeater sites. Static GeoJSON bundled
    // with the app (parsed offline from the 2013 NIFC Aviation
    // Frequency Guide PDF). ~180 features, ~186 KB — small enough
    // to ship as a public asset and have the service worker cache
    // it offline along with the rest of the shell.
    id: 'repeaters',
    sourceId: 'repeaters',
    statusKey: 'repeaters',
    buildUrl: () => '/data/nifc-repeaters.geojson',
  },
  {
    // FAA Class B / C / D controlled airspace. Bbox-filtered via
    // /api/airspace-class (proxies FAA AIS Airspace FeatureServer).
    // 24-hour cache (same 56-day FAA update cycle as SUA).
    id: 'classBCD',
    sourceId: 'classBCD',
    statusKey: 'classBCD',
    buildUrl: (m) => `/api/airspace-class?bbox=${encodeURIComponent(bboxWsEn(m))}`,
  },
  {
    // FAA ATS Route centerlines (Victor / Jet / RNAV). Bbox-filtered
    // via /api/airways (proxies FAA AIS ATS_Route FeatureServer).
    // 56-day cache (NASR cycle).
    id: 'airways',
    sourceId: 'airways',
    statusKey: 'airways',
    buildUrl: (m) => `/api/airways?bbox=${encodeURIComponent(bboxWsEn(m))}`,
  },
  {
    id: 'airport-da',
    sourceId: 'airport-da',
    statusKey: 'airport-da',
    buildUrl: (m) => `/api/wx/airport-da?bbox=${encodeURIComponent(bboxWsEn(m))}`,
  },
];

/** Quick lookup by OverlayId. */
export const OVERLAY_BY_ID: ReadonlyMap<string, OverlaySpec> = new Map(
  OVERLAY_SPECS.map((s) => [s.id, s]),
);
